import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

/**
 * CE QUE L'ORG A INSTALLE, ET OU ELLE TOURNE, tels que le shell les lit.
 *
 * Deux documents de la base du client, depuis le 10/10/2026 (ils vivaient dans
 * `taki.config.json`) : `orgs/{orgId}/settings/apps` (la liste ordonnee des
 * apps installees, qui fait le dock) et `orgs/{orgId}/settings/environnement`
 * (`production` ou `staging`, la bande STAGING, et le fuseau horaire de l'org).
 * Le raisonnement : `docs/socle/apps-installees-en-base.md` et
 * `docs/socle/fuseau-org.md`.
 *
 * POURQUOI UN PORT, comme le branding (`lib/branding.tsx`) : `@taki/ui` et le
 * shell ne parlent jamais au SDK. L'hote observe les deux documents
 * (`sdk.appsInstallees`, `sdk.environnement`) et monte `InstallationProvider`.
 * SANS PROVIDER, AUCUNE APP N'EST INSTALLEE : il n'existe pas d'autre source,
 * et aucun repli.
 */

export type EnvironnementDeploiement = 'production' | 'staging';

export interface InstallationPort {
  /** Les ids des apps installees, dans l'ordre du document (celui du dock). */
  apps: readonly string[];
  /**
   * `false` tant que la liste n'a pas ete lue une premiere fois. L'hote peut
   * rendre la derniere liste vue sur ce poste en attendant (cache d'affichage,
   * remplace par la base des la premiere lecture).
   */
  pret: boolean;
  /** Le document existe-t-il ? `false` : aucune app n'a jamais ete posee pour cette org. */
  posee: boolean;
  environnement: EnvironnementDeploiement;
  /** Owner ou admin actif, hors org master. */
  peutGererLesApps: boolean;
  /** Owner actif, hors org master. */
  peutChangerLEnvironnement: boolean;
  /** Pourquoi le geste est grise, quand il l'est (regle G16 : griser avec sa raison). */
  raisonApps: string | null;
  raisonEnvironnement: string | null;
  installer: (appId: string) => Promise<void>;
  /** Masque l'app pour toute l'org ; aucune donnee n'est supprimee. */
  desinstaller: (appId: string) => Promise<void>;
  changerEnvironnement: (environnement: EnvironnementDeploiement) => Promise<void>;
  /** Le fuseau REGLE de l'org (nom IANA), `null` sans reglage : Europe/Luxembourg. */
  fuseau: string | null;
  /** Owner actif, l'org master comprise. */
  peutChangerLeFuseau: boolean;
  /** Regle le fuseau, ou le retire avec `null`. */
  changerFuseau: (fuseau: string | null) => Promise<void>;
}

const InstallationContext = createContext<InstallationPort | null>(null);

export function InstallationProvider({ value, children }: { value: InstallationPort; children: ReactNode }) {
  return <InstallationContext.Provider value={value}>{children}</InstallationContext.Provider>;
}

/** Le port d'installation, ou `null` sans provider (et donc aucune app). */
export function useInstallationPort(): InstallationPort | null {
  return useContext(InstallationContext);
}
